import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, GraduationCap, UsersRound, Library,
  ClipboardList, BarChart3, Settings, FileQuestion, FilePlus2,
  TrendingUp, Award, CalendarClock, LogOut, User, BookOpen, ShieldCheck, History,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoMark, BRAND } from "@/components/Logo";

const linksByRole = {
  admin: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/admins", label: "Admins", icon: ShieldCheck },
    { to: "/admin/trainers", label: "Trainers", icon: UsersRound },
    { to: "/admin/students", label: "Employees", icon: GraduationCap },
    { to: "/admin/courses", label: "Courses", icon: Library },
    { to: "/admin/batches", label: "Batches", icon: BookOpen },
    { to: "/admin/assessments", label: "Assessments", icon: ClipboardList },
    { to: "/admin/reports", label: "Reports", icon: BarChart3 },
    { to: "/admin/audit-logs", label: "Audit Logs", icon: History },
    { to: "/admin/settings", label: "Settings", icon: Settings },
  ],
  trainer: [
    { to: "/trainer", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/trainer/questions", label: "Question Bank", icon: FileQuestion },
    { to: "/trainer/create-test", label: "Create Test", icon: FilePlus2 },
    { to: "/trainer/results", label: "Results", icon: ClipboardList },
    { to: "/trainer/reports", label: "Reports", icon: BarChart3 },
    { to: "/trainer/analytics", label: "Analytics", icon: TrendingUp },
  ],
  student: [
    { to: "/student", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/student/courses", label: "My Courses", icon: Library },
    { to: "/student/upcoming", label: "Upcoming Tests", icon: CalendarClock },
    { to: "/student/results", label: "Results", icon: ClipboardList },
    { to: "/student/certificates", label: "Certificates", icon: Award },
  ],
};

const roleLabel = {
  admin: "Admin Console",
  trainer: "Trainer Hub",
  student: "Employee Portal",
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const links = linksByRole[user.role] || [];

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <aside
        data-testid="sidebar"
        className="w-64 bg-white border-r border-slate-200 flex flex-col flex-shrink-0 z-20"
      >
        <div className="h-16 border-b border-slate-200 flex items-center px-5 gap-2.5">
          <LogoMark size="sm" />
          <div className="leading-tight min-w-0">
            <div className="text-base font-semibold text-slate-900 truncate">{BRAND.short}</div>
            <div className="text-[10px] tracking-wider uppercase text-slate-500 truncate">
              {roleLabel[user.role]}
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {links.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`sidebar-nav-${user.role}-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-blue-50 text-blue-700 border-r-2 border-blue-600"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-200 p-3">
          <button
            data-testid="profile-link"
            onClick={() => navigate(`/${user.role}/profile`)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 text-left transition-colors"
          >
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt=""
                className="w-9 h-9 rounded-full object-cover border border-slate-200"
                data-testid="sidebar-avatar-img"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center text-slate-600">
                <User className="w-4 h-4" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate" data-testid="user-name">
                {user.name}
              </div>
              <div className="text-xs text-slate-500 truncate">{user.email}</div>
            </div>
          </button>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto animate-fade-up">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
